import { Suspense } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/base/ScrollToTop';
import { SEO } from '../../utils/seo';

const FlowPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "就労支援サービスご利用の流れ",
    "description": "おかやま就労支援事業所のサービス利用開始までの6つのステップ",
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "お問い合わせ",
        "text": "お電話またはお問い合わせフォームからご連絡ください"
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "見学・相談",
        "text": "事業所の雰囲気を実際に見ていただき、サービス内容について詳しくご説明します"
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "受給者証の申請",
        "text": "市区町村の障害福祉課で障害福祉サービス受給者証の申請を行います"
      },
      {
        "@type": "HowToStep",
        "position": 4,
        "name": "体験利用",
        "text": "実際のプログラムを体験していただけます"
      },
      {
        "@type": "HowToStep",
        "position": 5,
        "name": "利用契約・利用開始",
        "text": "受給者証が発行されましたら利用契約を結び、サービスの利用を開始します"
      },
      {
        "@type": "HowToStep",
        "position": 6,
        "name": "定期面談・就職活動支援",
        "text": "月1回の定期面談で進捗を確認し、就職活動を全面的にサポートします"
      }
    ]
  };

  const steps = [
    {
      id: 1,
      number: '01',
      title: 'お問い合わせ',
      description:
        'まずはお電話またはお問い合わせフォームからご連絡ください。ご質問やご相談にも丁寧にお答えします。お気軽にお問い合わせください。',
    },
    {
      id: 2,
      number: '02',
      title: '見学・相談',
      description:
        '事業所の雰囲気を実際に見ていただき、サービス内容について詳しくご説明します。ご本人様やご家族のご希望や不安なことなど、何でもお聞かせください。',
    },
    {
      id: 3,
      number: '03',
      title: '受給者証の申請',
      description:
        'お住まいの市区町村の障害福祉課で、障害福祉サービス受給者証の申請を行います。申請方法や必要書類についても丁寧にサポートいたします。',
    },
    {
      id: 4,
      number: '04',
      title: '体験利用',
      description:
        '実際のプログラムを体験していただけます。通所のペースや雰囲気を確認し、納得してからご利用を開始していただけます。',
    },
    {
      id: 5,
      number: '05',
      title: '利用契約・利用開始',
      description:
        '受給者証が発行されましたら、利用契約を結びます。個別支援計画を作成し、あなたに合ったペースでサービスの利用を開始します。',
    },
    {
      id: 6,
      number: '06',
      title: '定期面談・就職活動支援',
      description:
        '月1回の定期面談で進捗を確認し、計画を見直します。就職の準備が整ったら、履歴書作成や面接対策など、就職活動を全面的にサポートします。',
    },
  ];

  const pricingData = [
    { income: '生活保護受給世帯', fee: '0円' },
    { income: '市町村民税非課税世帯', fee: '0円' },
    { income: '市町村民税課税世帯（所得割16万円未満）', fee: '9,300円' },
    { income: '上記以外', fee: '37,200円' },
  ];

  const faqs = [
    {
      id: 1,
      question: '利用開始までどのくらいの期間がかかりますか？',
      answer:
        '受給者証の申請から発行まで、通常1〜2ヶ月程度かかります。ただし、自治体によって異なる場合がありますので、詳しくはお問い合わせください。',
    },
    {
      id: 2,
      question: '見学や体験利用は何回でもできますか？',
      answer:
        'はい、納得していただけるまで何度でも見学や体験利用が可能です。ご自身のペースでご検討ください。',
    },
    {
      id: 3,
      question: '利用料以外に費用はかかりますか？',
      answer:
        '基本的に利用料以外の費用はかかりません。ただし、外出プログラムなどで交通費や食事代が発生する場合は、実費をご負担いただきます。',
    },
  ];

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SEO
        title="ご利用の流れ・料金｜6ステップで安心スタート｜おかやま就労支援事業所"
        description="おかやま就労支援事業所のご利用の流れと料金について。お問い合わせから利用開始まで6つのステップで安心してスタート。世帯収入に応じた自己負担額で、多くの方が無料または低額でご利用可能。見学・体験利用随時受付中。"
        keywords="利用の流れ,料金,費用,受給者証,見学,体験利用,岡山市"
        schema={schema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-bg-ivory">
        <Header />
        <main>
          {/* Hero Section */}
          <section className="relative h-96 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 w-full h-full">
              <img
                src="https://public.readdy.ai/ai/img_res/410d0cbbaf60afdf45bd088aafa6f728.jpg"
                alt="ご利用の流れ"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
            </div>
            <div className="relative z-10 text-center text-white space-y-4">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">
                ご利用の流れ
              </h1>
              <p className="text-sm text-white/80">トップ &gt; ご利用の流れ</p>
            </div>
          </section>

          {/* Flow Steps Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 lg:mb-16">
                <p className="text-primary text-sm font-medium tracking-wider mb-3">
                  （ご利用までの流れ）
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-text-dark">
                  6つのステップで<br className="sm:hidden" />
                  <strong>安心スタート</strong>
                </h2>
              </div>

              <div className="space-y-8">
                {steps.map((step, index) => (
                  <div key={step.id}>
                    <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow">
                      <div className="flex items-start gap-6">
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary flex items-center justify-center">
                            <span className="text-white text-xl sm:text-2xl font-bold">
                              {step.number}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 pt-2">
                          <h3 className="text-xl sm:text-2xl font-bold text-text-dark mb-3">
                            {step.title}
                          </h3>
                          <p className="text-text-medium leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="flex justify-center py-4">
                        <i className="ri-arrow-down-line text-3xl text-primary"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-bg-beige">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
                <div className="lg:col-span-2">
                  <h2 className="font-serif text-3xl sm:text-4xl font-bold text-text-dark mb-6">
                    ご利用料金について
                  </h2>
                  <div className="space-y-4 text-text-medium leading-relaxed">
                    <p>
                      <strong>障害福祉サービス受給者証</strong>をお持ちの方は、世帯収入に応じた自己負担額となります。
                    </p>
                    <p>
                      多くの方が無料または低額でご利用いただけます。詳しくはお問い合わせください。
                    </p>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-primary text-white">
                          <th className="px-6 py-4 text-left font-bold">世帯収入</th>
                          <th className="px-6 py-4 text-left font-bold">月額上限</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingData.map((item, index) => (
                          <tr key={index} className="border-b border-gray-200 last:border-b-0">
                            <td className="px-6 py-4 text-text-dark">{item.income}</td>
                            <td className="px-6 py-4 text-primary font-bold text-lg">
                              {item.fee}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-text-medium text-sm mt-4">
                    ※上記は月額の自己負担上限額です。実際の利用日数に応じて計算されます。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-text-dark">
                  よくある質問
                </h2>
              </div>

              <div className="space-y-6">
                {faqs.map((faq) => (
                  <div
                    key={faq.id}
                    className="bg-bg-beige rounded-xl p-6 sm:p-8 shadow-md"
                  >
                    <h3 className="font-bold text-text-dark text-lg mb-3 flex items-start">
                      <span className="text-primary mr-2">Q.</span>
                      {faq.question}
                    </h3>
                    <p className="text-text-medium leading-relaxed pl-6">
                      <span className="text-primary font-bold mr-2">A.</span>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-12 text-center">
                <p className="text-text-medium mb-6">
                  その他ご不明な点がございましたら、お気軽にお問い合わせください。
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-mail-line mr-2"></i>
                  お問い合わせフォームへ
                </a>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </Suspense>
  );
};

export default FlowPage;
